import { assetUrl } from '../http/build-url';
import {
  asBoolean,
  asNumber,
  asString,
  asStringMap,
  asStringSet,
} from '../http/normalise';
import {
  ApiAccessZone,
  ApiAttendee,
  ApiBadge,
  ApiCheckInEvent,
  ApiScannerManifest,
  ApiScannerProfile,
  ApiTicketType,
} from './access.dto';
import {
  AccessVerdict,
  AccessZone,
  Attendee,
  Badge,
  CheckInEvent,
  DenyReason,
  ManifestAttendee,
  ManifestZone,
  ScannerManifest,
  ScannerProfile,
  TicketType,
} from './access.model';

const DENY_REASONS: ReadonlySet<string> = new Set<string>([
  'WRONG_EVENT',
  'WRONG_TICKET_TYPE',
  'ZONE_NOT_ON_PROFILE',
  'EVENT_NOT_LIVE',
  'EXPIRED',
  'UNKNOWN_CODE',
  'ZONE_FULL',
  'ALREADY_INSIDE',
]);

function asVerdict(value: unknown): AccessVerdict {
  return value === 'GRANTED' ? 'GRANTED' : 'DENIED';
}

function asDenyReason(value: unknown): DenyReason | null {
  if (typeof value === 'string' && DENY_REASONS.has(value)) {
    return value as DenyReason;
  }
  return null;
}

export function mapTicketType(dto: ApiTicketType): TicketType {
  return {
    id: asString(dto.id) ?? '',
    eventId: asString(dto.eventId) ?? '',
    name: asString(dto.name) ?? '',
    colorHex: asString(dto.colorHex),
  };
}

export function mapAttendee(dto: ApiAttendee, storageBase: string): Attendee {
  return {
    id: asString(dto.id) ?? '',
    eventId: asString(dto.eventId) ?? '',
    firstName: asString(dto.firstName) ?? '',
    lastName: asString(dto.lastName) ?? '',
    email: asString(dto.email) ?? '',
    company: asString(dto.company),
    ticketTypeId: asString(dto.ticketTypeId),
    photoUrl: assetUrl(storageBase, dto.photoUrl),
    qrToken: asString(dto.qrToken) ?? '',
    customFields: asStringMap(dto.customFields),
    checkedInAt: asString(dto.checkedInAt),
  };
}

export function mapAccessZone(dto: ApiAccessZone): AccessZone {
  return {
    id: asString(dto.id) ?? '',
    eventId: asString(dto.eventId) ?? '',
    name: asString(dto.name) ?? '',
    allowedTicketTypeIds: asStringSet(dto.allowedTicketTypes),
    roomId: asString(dto.roomId),
    capacityLimit: asNumber(dto.capacityLimit),
    currentOccupancy: asNumber(dto.currentOccupancy) ?? 0,
  };
}

export function mapScannerProfile(dto: ApiScannerProfile): ScannerProfile {
  return {
    id: asString(dto.id) ?? '',
    eventId: asString(dto.eventId) ?? '',
    name: asString(dto.name) ?? '',
    zoneIds: asStringSet(dto.zoneIds),
    assignedUserId: asString(dto.assignedUserId),
    assignedDeviceLabel: asString(dto.assignedDeviceLabel),
  };
}

export function mapCheckInEvent(dto: ApiCheckInEvent): CheckInEvent {
  const id = asString(dto.id) ?? '';

  return {
    id,
    idempotencyKey: asString(dto.idempotencyKey) ?? id,
    eventId: asString(dto.eventId) ?? '',
    attendeeId: asString(dto.attendeeId) ?? '',
    zoneId: asString(dto.zoneId) ?? '',
    scannerProfileId: asString(dto.scannerProfileId) ?? '',
    scannedAt: asString(dto.scannedAt) ?? '',
    syncedAt: asString(dto.syncedAt),
    result: asVerdict(dto.result),
    denyReason: asDenyReason(dto.denyReason),
    // What the scanner decided offline can disagree with what the server decides on sync; the
    // server's verdict is authoritative and `conflict` is how the log shows the two apart.
    serverResult:
      dto.serverResult === undefined ? null : asVerdict(dto.serverResult),
    serverDenyReason: asDenyReason(dto.serverDenyReason),
    conflict: asBoolean(dto.conflict, false),
  };
}

export function mapBadge(dto: ApiBadge, storageBase: string): Badge {
  return {
    token: asString(dto.token) ?? '',
    expiresAt: asString(dto.expiresAt),
    name: asString(dto.displayFields?.name) ?? '',
    ticketType: asString(dto.displayFields?.ticketType),
    company: asString(dto.displayFields?.company),
    photoUrl: assetUrl(storageBase, dto.displayFields?.photoUrl),
  };
}

function mapManifestZone(
  dto: NonNullable<ApiScannerManifest['zones']>[number]
): ManifestZone {
  return {
    id: asString(dto.id) ?? '',
    name: asString(dto.name) ?? '',
    roomId: asString(dto.roomId),
    allowedTicketTypeIds: asStringSet(dto.allowedTicketTypeIds),
    capacityLimit: asNumber(dto.capacityLimit),
  };
}

/**
 * The manifest abbreviates its attendee rows — `n`, `t`, `v` — because it is downloaded onto a
 * scanner over whatever connection the venue has. Names are restored here, once.
 */
function mapManifestAttendee(
  dto: NonNullable<ApiScannerManifest['attendees']>[number]
): ManifestAttendee {
  return {
    id: asString(dto.id) ?? '',
    name: asString(dto.n) ?? '',
    ticketTypeId: asString(dto.t),
    version: asNumber(dto.v) ?? 0,
  };
}

export function mapScannerManifest(dto: ApiScannerManifest): ScannerManifest {
  return {
    eventId: asString(dto.eventId) ?? '',
    profileId: asString(dto.profileId) ?? '',
    issuedAt: asString(dto.issuedAt) ?? '',
    expiresAt: asString(dto.expiresAt),
    badgePublicKey: asString(dto.badgePublicKey),
    eventPublished: asBoolean(dto.eventPublished, false),
    eventStartDate: asString(dto.eventStartDate),
    eventEndDate: asString(dto.eventEndDate),
    eventTimezone: asString(dto.eventTimezone),
    zones: (dto.zones ?? []).map(mapManifestZone),
    attendees: (dto.attendees ?? []).map(mapManifestAttendee),
  };
}
